/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import handleClickInLegend from "components/charts/modern/helpers/handleClickInLegend";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesColumnOptions } from "highcharts/highstock";
import SharesholdersHistoryQuery from "lib/graphql/queries/SharesholdersHistoryQuery";
import type {
  ISharesholdersHistoryQuery,
  ISharesholdersHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/SharesholdersHistoryQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import useTheme from "components/ui/useTheme";

interface SharesholdersChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function SharesholdersChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: SharesholdersChartProps) {
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);

  const intl = useIntl();
  const theme = useTheme();

  // Theme-aware colors
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const legendItemHoverColor = theme === "dark" ? colors.slate[400] : colors.slate[500];

  const tickerSymbolsForSeries = [...tickerSymbols];
  const selectedTickerSymbol = tickerSymbolsForSeries[0];
  if (!selectedTickerSymbol) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }
  const { data, previousData, loading } = useQuery<ISharesholdersHistoryQuery, ISharesholdersHistoryQueryVariables>(
    SharesholdersHistoryQuery,
    {
      variables: {
        tickerSymbol: tickerSymbols[0],
      },
    },
  );
  const sharesholdersHistory =
    (loading && !data ? previousData?.sharesholdersHistory : data?.sharesholdersHistory) || [];

  const hasSharesholdersHistory = sharesholdersHistory.length > 0;

  if (!loading && sharesholdersHistory.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const labels = {
    totalOfCorporateShareholders: intl.formatMessage({
      defaultMessage: "Pessoa Jurídica",
      description: "Gráfico de investidores para as empresas brasileiras",
      id: "fm/h0U",
    }),
    totalOfIndividualShareholders: intl.formatMessage({
      defaultMessage: "Pessoa Física",
      description: "Gráfico de investidores para as empresas brasileiras",
      id: "pgTZYi",
    }),
    totalOfInstitutionalShareholders: intl.formatMessage({
      defaultMessage: "Institucionais",
      description: "Gráfico de investidores para as empresas brasileiras",
      id: "e87rkJ",
    }),
  };

  const nodes = [
    {
      name: labels.totalOfCorporateShareholders,
      data: sharesholdersHistory
        .filter((node) => node.totalOfCorporateShareholders !== null)
        .map((node) => {
          return {
            x: getYear(parseISO(node.asOfDate)),
            y: node.totalOfCorporateShareholders,
            custom: node,
          };
        }),
    },
    {
      name: labels.totalOfIndividualShareholders,
      data: sharesholdersHistory
        .filter((node) => node.totalOfIndividualShareholders !== null)
        .map((node) => {
          return {
            x: getYear(parseISO(node.asOfDate)),
            y: node.totalOfIndividualShareholders,
            custom: node,
          };
        }),
    },
    {
      name: labels.totalOfInstitutionalShareholders,
      data: sharesholdersHistory
        .filter((node) => node.totalOfInstitutionalShareholders !== null)
        .map((node) => {
          return {
            x: getYear(parseISO(node.asOfDate)),
            y: node.totalOfInstitutionalShareholders,
            custom: node,
          };
        }),
    },
  ];

  const series = nodes.map((node, index) => {
    const colorTheme = getColorThemeForIndex(index, theme);

    if (!colorTheme) {
      throw new Error("Theme not found.");
    }

    return {
      showInNavigator: true,
      type: "column",
      animation: false,
      name: node.name,
      color: colorTheme.color,
      lineColor: colorTheme.lineColor,
      lineWidth: 2,
      borderColor: colorTheme.lineColor,
      borderWidth: 1.5,
      stacking: "normal",
      states: {
        hover: {
          color: colorTheme.lineColor,
        },
      },
      data: node.data.sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: selectedTickerSymbol,
        primaryColor: colorTheme.color,
        secondaryColor: colorTheme.lineColor,
      },
    };
  }) as SeriesColumnOptions[];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 0,
      alignColumns: true,
      itemDistance: 16,
      itemStyle: {
        color: labelsColor,
      },
      itemHoverStyle: {
        color: legendItemHoverColor,
      },
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        format: "{value}",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      type: "linear",
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter: function () {
          return intl.formatNumber(parseFloat(String(this.value)), {
            notation: "compact",
            minimumIntegerDigits: 1,
            minimumFractionDigits: 1,
            maximumFractionDigits: 0,
            minimumSignificantDigits: 1,
            maximumSignificantDigits: 4,
          });
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        if (!this.key || !this.points || this.points.length === 0) {
          return "";
        }

        const valueFormatter = new Intl.NumberFormat(intl.locale, {
          notation: "compact",
          compactDisplay: "short",
          minimumSignificantDigits: 3,
          maximumSignificantDigits: 4,
        });

        const keyValues = this.points
          .sort((a, b) => {
            return (b.y || 0) - (a.y || 0);
          })
          .map(({ series, y }) => {
            if (!y) {
              return null;
            }

            return {
              key: series.name,
              value: valueFormatter.format(y),
              yValue: y,
              color: series.userOptions.custom ? series.userOptions.custom.secondaryColor : undefined,
            };
          })
          .filter(notUndefinedOrNull);

        return renderToStaticMarkup(<KeyValueTooltip x={String(this.key)} keyValues={keyValues} />);
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick(event) {
            handleClickInLegend(event, this, chartRef);
          },
        },
      },
      column: {
        clip: true,
        borderRadius: {
          where: "end",
          radius: 8,
          scope: "stack",
        },
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
  };

  if (loading && sharesholdersHistory.length === 0) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  return (
    <OnlyPremiumRender>
      <div className="flex h-full w-full flex-col space-y-4">
        <div className="flex flex-col items-center justify-between sm:flex-row">
          <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
            {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
              <TickerSymbolSelector
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            )}
          </div>
        </div>

        {hasSharesholdersHistory ? (
          <HighchartsChart ref={chartRef} options={options} className="h-96 xl:h-full" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
              <div>
                <SadFaceIcon />
              </div>

              <p>
                <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
              </p>
            </div>
          </div>
        )}
      </div>
    </OnlyPremiumRender>
  );
}
